<template>
	<CardHeader
		:class="cn(propertyCardHeaderBodyVariants({ size, variant }), props.class)"
	>
		<CardCarousel
			:size="size"
			:variant="variant"
			:is-mouse-over-card-header="isMouseOverCardHeader"
			:images="property.images"
			:dots-variants="dotsVariants"
			@image-scrolled="handlePropertyImageScrolled"
		>
			<template #default="{ image, index }">
				<CardCarouselImage
					:index="index"
					:image="image"
					:variant="variant"
					:image-alt="getImageAlt(index)"
					:is-property-active="props.isPropertyActive"
				/>
			</template>
		</CardCarousel>

		<div class="absolute top-0 z-20 flex w-full justify-between p-4">
			<Badge
				v-if="shouldShowExclusiveBadge"
				class="size-fit text-xs font-medium tracking-wider"
			>
				Exclusivo
			</Badge>
		</div>

		<slot />
	</CardHeader>
</template>

<script setup lang="ts">
import type { PropertyCardHeaderProps } from '.';
import type { ImageScrolledEvent } from '../Card/CardCarousel';
import type { PropertyImage } from '~/core/entities/property';
import type { PropertyCardImageScrolled, SectionName } from '~/types/mixpanel';

import { cn } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isNumber } from '~~/utils';
import { computed } from 'vue';

import {
	propertyCardCarouselDotsVariants,
	propertyCardHeaderBodyVariants
} from '.';
import CardCarousel from '../Card/CardCarousel/Carousel.vue';
import CardCarouselImage from '../Card/CardCarouselImage/CarouselImage.vue';
import { Badge } from '../ui/badge';
import { CardHeader } from '../ui/card';

export interface CarouselItem extends PropertyImage {}

const props = defineProps<PropertyCardHeaderProps>();

const route = useRoute();
const { trackPropertyImageScrolled } = useEvents();

const section = computed<SectionName | undefined>(() => {
	if (props.section) {
		return props.section;
	}

	if (route.fullPath.startsWith('/venda/mapa-de-imoveis')) {
		return 'listed properties';
	}

	return undefined;
});

const shouldShowExclusiveBadge = computed(
	() => props.property.isExclusive && props.isPropertyActive
);

const trackingSharedProps = computed<
	Omit<PropertyCardImageScrolled, 'click_count'>
>(() => ({
	[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
	[MixpanelProps.CURRENT_PAGE]: route.fullPath,
	[MixpanelProps.SECTION_NAME]: section.value || '',
	[MixpanelProps.CARD_POSITION]: isNumber(props.cardPosition)
		? props.cardPosition
		: undefined,
	[MixpanelProps.CARD_TYPE]:
		props.variant === 'fullFace' ? 'full_face' : 'regular_card',
	[MixpanelProps.COMPANY_NAME]: props.property.company.name,
	[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive,
	[MixpanelProps.AGENT_NAME]: props.property.agent.name
}));

const dotsVariants = computed(
	() => (variantProps: any) =>
		cn(
			propertyCardCarouselDotsVariants({
				...variantProps,
				extendedBottom: !!props.property.poiDistanceTime
			}),
			props.dotClasses,
			{ 'bottom-1': !props.isPropertyActive }
		)
);

function handlePropertyImageScrolled(event: ImageScrolledEvent) {
	trackPropertyImageScrolled({
		[MixpanelProps.CLICK_COUNT]: event.clickCount,
		...trackingSharedProps.value
	});
}

function getImageAlt(index: number) {
	return getPropertyImageAlt(
		props.property.propertyType?.name,
		props.property.suites,
		props.property.region,
		props.property.city,
		props.property.askingPrice,
		index
	);
}
</script>
